/**
 * Group Editor Component
 * 
 * Edits logical groups (AND/OR/NOT) with nested conditions
 */

import { useCallback } from "react";
import {
  Card,
  BlockStack,
  InlineStack,
  Select,
  Button,
  Text,
  Banner,
} from "@shopify/polaris";
import { PlusIcon, DeleteIcon } from "@shopify/polaris-icons";
import type { LogicalCondition, Condition, ValidationError } from "./types";
import { LOGICAL_OPERATORS } from "./types";
import { ConditionEditor } from "./ConditionEditor";

interface GroupEditorProps {
  group: LogicalCondition;
  onChange: (group: LogicalCondition) => void;
  onDelete?: () => void;
  errors?: ValidationError[];
  path: string;
  depth?: number;
}

export function GroupEditor({
  group,
  onChange,
  onDelete,
  errors = [],
  path,
  depth = 0,
}: GroupEditorProps) {
  const isRootGroup = depth === 0;
  const maxDepth = 5; // Prevent infinite nesting

  // Get errors for this group
  const groupErrors = errors.filter((e) => e.path.startsWith(path));

  /**
   * Handle group type change (AND/OR/NOT)
   */
  const handleTypeChange = useCallback(
    (newType: string) => {
      const updatedGroup: LogicalCondition = {
        ...group,
        type: newType as "and" | "or" | "not",
      };

      // NOT can only have 1 condition
      if (newType === "not" && updatedGroup.conditions.length > 1) {
        updatedGroup.conditions = [updatedGroup.conditions[0]];
      }

      onChange(updatedGroup);
    },
    [group, onChange]
  );

  /**
   * Add a new simple condition
   */
  const handleAddCondition = useCallback(() => {
    const newCondition: Condition = {
      type: "customer_total_spent",
      operator: "gt",
      value: 100,
    };

    onChange({
      ...group,
      conditions: [...group.conditions, newCondition],
    });
  }, [group, onChange]);

  /**
   * Add a new logical group
   */
  const handleAddGroup = useCallback(() => {
    const newGroup: LogicalCondition = {
      type: "and",
      conditions: [],
    };

    onChange({
      ...group,
      conditions: [...group.conditions, newGroup],
    });
  }, [group, onChange]);

  /**
   * Update a child condition
   */
  const handleConditionChange = useCallback(
    (index: number, newCondition: Condition) => {
      const newConditions = [...group.conditions];
      newConditions[index] = newCondition;
      onChange({
        ...group,
        conditions: newConditions,
      });
    },
    [group, onChange]
  );

  /**
   * Delete a child condition
   */
  const handleConditionDelete = useCallback(
    (index: number) => {
      const newConditions = group.conditions.filter((_: Condition, i: number) => i !== index);
      onChange({
        ...group,
        conditions: newConditions,
      });
    },
    [group, onChange]
  );

  /**
   * Check if a condition is a logical group
   */
  const isLogicalGroup = (condition: Condition): condition is LogicalCondition => {
    return "conditions" in condition && Array.isArray(condition.conditions);
  };

  /**
   * Render a child condition or group
   */
  const renderCondition = (condition: Condition, index: number) => {
    const childPath = `${path}.conditions[${index}]`;

    if (isLogicalGroup(condition)) {
      return (
        <GroupEditor
          key={index}
          group={condition}
          onChange={(updated) => handleConditionChange(index, updated)}
          onDelete={() => handleConditionDelete(index)}
          errors={errors}
          path={childPath}
          depth={depth + 1}
        />
      );
    }

    return (
      <ConditionEditor
        key={index}
        condition={condition}
        onChange={(updated) => handleConditionChange(index, updated)}
        onDelete={() => handleConditionDelete(index)}
        errors={errors}
        path={childPath}
      />
    );
  };

  const canAddGroup = depth < maxDepth;
  const canAddCondition = group.type !== "not" || group.conditions.length === 0;

  return (
    <Card>
      <BlockStack gap="400">
        {groupErrors.length > 0 && (
          <Banner tone="critical">
            <BlockStack gap="100">
              {groupErrors.map((error, idx) => (
                <Text key={idx} as="p">
                  {error.message}
                </Text>
              ))}
            </BlockStack>
          </Banner>
        )}

        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Text as="h3" variant="headingMd">
              {isRootGroup ? "Targeting Rules" : "Group"}
            </Text>
            <Select
              label=""
              labelHidden
              options={LOGICAL_OPERATORS.map((op) => ({
                label: op.label,
                value: op.value,
              }))}
              value={group.type}
              onChange={handleTypeChange}
            />
          </InlineStack>

          {!isRootGroup && onDelete && (
            <Button
              icon={DeleteIcon}
              onClick={onDelete}
              variant="plain"
              tone="critical"
              accessibilityLabel="Delete group"
            />
          )}
        </InlineStack>

        <Text as="p" tone="subdued">
          {LOGICAL_OPERATORS.find((op) => op.value === group.type)?.description}
        </Text>

        {group.type === "not" && group.conditions.length === 0 && (
          <Banner tone="warning">
            NOT groups require exactly one condition or sub-group
          </Banner>
        )}

        {group.type === "not" && group.conditions.length > 1 && (
          <Banner tone="critical">
            NOT groups can only have one condition or sub-group
          </Banner>
        )}

        <BlockStack gap="300">
          {group.conditions.map((condition: Condition, index: number) => renderCondition(condition, index))}

          {group.conditions.length === 0 && (
            <Banner tone="info">
              Add conditions or groups to define targeting rules
            </Banner>
          )}
        </BlockStack>

        <InlineStack gap="200">
          {canAddCondition && (
            <Button
              icon={PlusIcon}
              onClick={handleAddCondition}
            >
              Add Condition
            </Button>
          )}

          {canAddGroup && (
            <Button
              icon={PlusIcon}
              onClick={handleAddGroup}
              variant="plain"
            >
              Add Group
            </Button>
          )}
        </InlineStack>

        {!canAddCondition && group.type === "not" && (
          <Text as="p" tone="subdued">
            NOT groups can only contain one condition or sub-group
          </Text>
        )}

        {!canAddGroup && (
          <Text as="p" tone="subdued">
            Maximum nesting depth reached
          </Text>
        )}
      </BlockStack>
    </Card>
  );
}
